/**
 * Grid Context for managing grid state and operations
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { 
  GridLayout, 
  GridTile, 
  GridPosition, 
  GridSize, 
  GridHeader,
  GridCategory,

  migrateLegacyHeaders,
  ensureHeadersExist,
  findCollisionFreePosition,
  findRestorationPosition,
  canPlaceTile,

  createDefaultHeaders,
  ensureUniqueHeaderIds,
  adjustTilesForRowInsertion,
  adjustTilesForColumnInsertion,
  adjustTilesForRowRemoval,
  adjustTilesForColumnRemoval,
  adjustHeadersForRowInsertion,
  adjustHeadersForColumnInsertion,
  adjustHeadersForRowRemoval,
  adjustHeadersForColumnRemoval,
  adjustCategoriesForRowInsertion,
  adjustCategoriesForColumnInsertion,
  adjustCategoriesForRowRemoval,
  adjustCategoriesForColumnRemoval,
  resolveCollisionsAfterInsertion
} from '../lib/gridUtils';

export type GridMode = 'read' | 'edit';

export type SelectionType = 'none' | 'tile' | 'row' | 'column' | 'rowHeader' | 'columnHeader' | 'rowCategory' | 'columnCategory';

export interface GridSelection {
  type: SelectionType;
  id?: string;
  index?: number;
  headerId?: string;
  categoryId?: string;
}

export interface GridState {
  layout: GridLayout;
  tiles: GridTile[];
  mode: GridMode;
  selection: GridSelection;
  selectedTiles: string[]; // Multi-select support - array of tile IDs
  draggedTile?: {
    id: string;
    startPosition: GridPosition;
    currentPosition: GridPosition;
  };
  resizingTile?: {
    id: string;
    startSize: GridSize;
    currentSize: GridSize;
    startPosition: GridPosition;
    currentPosition: GridPosition;
  };
  isLoading: boolean;
  hasUnsavedChanges: boolean;
  history: {
    past: GridHistoryState[];
    present: GridHistoryState;
    future: GridHistoryState[];
  };
  // Tile protection system
  protectedTiles: Set<string>; // Tiles currently being edited
  contentEditingTile?: string; // Currently editing tile ID
  // Navigation warning system
  navigationWarning?: {
    isOpen: boolean;
    destinationUrl: string;
    destinationTitle?: string;
    isSaving?: boolean;
  };
}

export interface GridHistoryState {
  layout: GridLayout;
  tiles: GridTile[];
  timestamp: number;
  action: string;
}

export type GridAction =
  | { type: 'SET_MODE'; payload: GridMode }
  | { type: 'SET_LAYOUT'; payload: GridLayout }
  | { type: 'SET_TILES'; payload: GridTile[] }
  | { type: 'ADD_TILE'; payload: GridTile }
  | { type: 'RESTORE_TILE'; payload: GridTile }
  | { type: 'UPDATE_TILE'; payload: { id: string; updates: Partial<GridTile> } }
  | { type: 'REMOVE_TILE'; payload: string }
  | { type: 'MOVE_TILE'; payload: { id: string; position: GridPosition } }
  | { type: 'RESIZE_TILE'; payload: { id: string; size: GridSize } }
  | { type: 'SET_SELECTION'; payload: GridSelection }
  | { type: 'TOGGLE_TILE_SELECTION'; payload: string } // Toggle tile in multi-select
  | { type: 'SET_SELECTED_TILES'; payload: string[] } // Set multi-select array
  | { type: 'CLEAR_SELECTED_TILES' } // Clear all selected tiles
  | { type: 'UPDATE_SELECTED_TILES'; payload: { updates: Partial<GridTile> } } // Batch update
  | { type: 'REMOVE_SELECTED_TILES' } // Batch delete
  | { type: 'MOVE_SELECTED_TILES'; payload: { targetPageId: string } } // Batch move
  | { type: 'START_DRAG'; payload: { id: string; position: GridPosition } }
  | { type: 'UPDATE_DRAG'; payload: GridPosition }
  | { type: 'END_DRAG' }
  | { type: 'START_RESIZE'; payload: { id: string; size: GridSize; position: GridPosition } }
  | { type: 'UPDATE_RESIZE'; payload: { size: GridSize; position: GridPosition } }
  | { type: 'END_RESIZE' }
  | { type: 'ADD_ROW'; payload?: number; appearanceDefaults?: any }
  | { type: 'REMOVE_ROW'; payload: number }
  | { type: 'ADD_COLUMN'; payload?: number; appearanceDefaults?: any }
  | { type: 'REMOVE_COLUMN'; payload: number }
  | { type: 'INSERT_ROW_AT'; payload: number; appearanceDefaults?: any }
  | { type: 'INSERT_COLUMN_AT'; payload: number; appearanceDefaults?: any }
  | { type: 'REMOVE_ROW_AT'; payload: number }
  | { type: 'REMOVE_COLUMN_AT'; payload: number }
  | { type: 'CLEANUP_HEADER_IDS' }
  | { type: 'TOGGLE_ROW_HEADERS'; payload?: boolean; appearanceDefaults?: any }
  | { type: 'TOGGLE_COLUMN_HEADERS'; payload?: boolean; appearanceDefaults?: any }
  | { type: 'UPDATE_HEADER'; payload: { type: 'row' | 'column'; header: GridHeader } }
  | { type: 'MERGE_HEADERS'; payload: { type: 'row' | 'column'; headerIds: string[]; newTitle: string } }
  | { type: 'SPLIT_HEADER'; payload: { type: 'row' | 'column'; headerId: string; splitIndex: number } }
  | { type: 'ADD_CATEGORY'; payload: { type: 'row' | 'column'; category: GridCategory } }
  | { type: 'UPDATE_CATEGORY'; payload: { type: 'row' | 'column'; category: GridCategory } }
  | { type: 'REMOVE_CATEGORY'; payload: { type: 'row' | 'column'; categoryId: string } }
  | { type: 'MIGRATE_LEGACY_HEADERS' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_UNSAVED_CHANGES'; payload: boolean }
  | { type: 'RESET_STATE'; payload: Partial<GridState> }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'CLEAR_HISTORY' }
  | { type: 'PROTECT_TILE'; payload: string }
  | { type: 'UNPROTECT_TILE'; payload: string }
  | { type: 'START_CONTENT_EDITING'; payload: string }
  | { type: 'END_CONTENT_EDITING' }
  | { type: 'SHOW_NAVIGATION_WARNING'; payload: { destinationUrl: string; destinationTitle?: string } }
  | { type: 'HIDE_NAVIGATION_WARNING' }
  | { type: 'SET_NAVIGATION_SAVING'; payload: boolean };

const initialState: GridState = {
  layout: { 
    rows: 3, 
    columns: 3, 
    gap: 16,
    showRowHeaders: false,
    showColumnHeaders: false,
    rowHeaders: [],
    columnHeaders: []
  },
  tiles: [],
  mode: 'read',
  selection: { type: 'none' },
  selectedTiles: [],
  isLoading: false,
  hasUnsavedChanges: false,
  protectedTiles: new Set(),
  contentEditingTile: undefined,
  history: {
    past: [],
    present: {
      layout: { 
        rows: 3, 
        columns: 3, 
        gap: 12,
        showRowHeaders: false,
        showColumnHeaders: false,
        rowHeaders: [],
        columnHeaders: []
      },
      tiles: [],
      timestamp: Date.now(),
      action: 'INITIAL'
    },
    future: []
  }
};

// Actions that should create history entries
const HISTORY_ACTIONS = new Set([
  'ADD_TILE',
  'RESTORE_TILE',
  'REMOVE_TILE',
  'MOVE_TILE',
  'RESIZE_TILE',
  'UPDATE_TILE',
  'ADD_ROW',
  'REMOVE_ROW',
  'ADD_COLUMN',
  'REMOVE_COLUMN',
  'INSERT_ROW_AT',
  'INSERT_COLUMN_AT',
  'REMOVE_ROW_AT',
  'REMOVE_COLUMN_AT',
  'SET_LAYOUT',
  'TOGGLE_ROW_HEADERS',
  'TOGGLE_COLUMN_HEADERS',
  'UPDATE_HEADER',
  'MERGE_HEADERS',
  'SPLIT_HEADER',
  'REMOVE_SELECTED_TILES'
]);

// Helper function to create history entry
function createHistoryEntry(state: GridState, action: string): GridHistoryState {
  return {
    layout: { ...state.layout },
    tiles: state.tiles.map(tile => ({
      ...tile,
      // Deep clone the config object to preserve content data
      config: tile.config ? JSON.parse(JSON.stringify(tile.config)) : tile.config,
      // Deep clone position and size objects as well
      position: { ...tile.position },
      size: { ...tile.size }
    })),
    timestamp: Date.now(),
    action
  };
}

// Helper function to add history entry
function addToHistory(state: GridState, action: string): GridState {
  if (!HISTORY_ACTIONS.has(action)) {
    return state;
  }

  const newPresent = createHistoryEntry(state, action);
  const maxHistorySize = 50;

  // Return the state with updated history, preserving the current state properties
  return {
    ...state,
    history: {
      past: [...state.history.past, state.history.present].slice(-maxHistorySize),
      present: newPresent,
      future: [] // Clear future when new action is performed
    }
  };
}

export function gridReducer(state: GridState, action: GridAction): GridState {
  // Track hasUnsavedChanges state changes for debugging
  const logStateChange = (actionType: string, oldValue: boolean, newValue: boolean) => {
    // Debug logging removed for cleaner console output
  };
  
  // Debug logging (can be removed after testing)
  // console.log(`🎯 GridContext Action: ${action.type} (current hasUnsavedChanges: ${state.hasUnsavedChanges})`);
  
  switch (action.type) {
    case 'SET_MODE':
      return {
        ...state,
        mode: action.payload,
        selection: action.payload === 'read' ? { type: 'none' } : state.selection,
      };

    case 'SET_LAYOUT':
      const setLayoutResult = {
        ...state,
        layout: action.payload,
        hasUnsavedChanges: true,
      };
      logStateChange('SET_LAYOUT', state.hasUnsavedChanges, setLayoutResult.hasUnsavedChanges);
      return setLayoutResult;

    case 'SET_TILES':
      // CRITICAL PROTECTION: Preserve protected tiles during SET_TILES
      
      let setTilesFinalTiles = action.payload;
      const setTilesProtectedTiles = state.protectedTiles || new Set();
      const setTilesEditingTile = state.contentEditingTile;
      
      if (setTilesProtectedTiles.size > 0 || setTilesEditingTile) {
        
        // Preserve protected tiles from current state
        const tilesToPreserve = state.tiles.filter(tile => 
          setTilesProtectedTiles.has(tile.id) || tile.id === setTilesEditingTile
        );
        
        
        // Merge with new tiles
        const newTileMap = new Map(action.payload.map(tile => [tile.id, tile]));
        tilesToPreserve.forEach(preservedTile => {
          newTileMap.set(preservedTile.id, preservedTile);
        });
        
        setTilesFinalTiles = Array.from(newTileMap.values());
        
      } else {
      }
      
      return {
        ...state,
        tiles: setTilesFinalTiles,
      };

    case 'ADD_TILE':
      if (
        action.payload.type === 'workflow_step' &&
        action.payload.position.column === 1 &&
        state.tiles.some(tile => tile.type === 'workflow_step' && tile.position.column === 1)
      ) {
        return state;
      }

      if (action.payload.type === 'workflow_bypass' && action.payload.position.column === 1) {
        return state;
      }

      // Use collision protection to find safe position for new tile
      const newTileCollisionResult = findCollisionFreePosition(
        state.layout,
        state.tiles,
        action.payload.position,
        action.payload.size
      );



      // Create the tile with collision-safe position
      const safeNewTile = {
        ...action.payload,
        position: newTileCollisionResult.position
      };
      
      let addTileState = {
        ...state,
        tiles: [...state.tiles, safeNewTile],
        hasUnsavedChanges: true,
      };

      // If grid expansion is needed, update the layout
      if (newTileCollisionResult.needsExpansion && newTileCollisionResult.expandedLayout) {
        addTileState = {
          ...addTileState,
          layout: newTileCollisionResult.expandedLayout
        };
      }
      

      
      return addToHistory(addTileState, 'ADD_TILE');

    case 'RESTORE_TILE':
      
      // Use collision protection specifically designed for restoration
      const restorationResult = findRestorationPosition(
        state.layout,
        state.tiles,
        action.payload.position,
        action.payload.size
      );



      // Create the restored tile with collision-safe position
      const restoredTile = {
        ...action.payload,
        position: restorationResult.position
      };
      
      let restoreTileState = {
        ...state,
        tiles: [...state.tiles, restoredTile],
        hasUnsavedChanges: true,
      };

      // If grid expansion is needed, update the layout
      if (restorationResult.needsExpansion && restorationResult.expandedLayout) {
        restoreTileState = {
          ...restoreTileState,
          layout: restorationResult.expandedLayout
        };
      }
      
      console.log('✅ RESTORE_TILE completed:', {
        newTilesCount: restoreTileState.tiles.length,
        finalPosition: restoredTile.position
      });
      
      return addToHistory(restoreTileState, 'RESTORE_TILE');

    case 'UPDATE_TILE': {
      const { id, updates } = action.payload;
      // CRITICAL: Log formId updates for debugging
      if (updates.config?.formId) {
      }


      const updatedTiles = state.tiles.map((tile) => {
        if (tile.id === id) {
          // Perform a deep merge for the config object
          const newConfig = updates.config
            ? { ...tile.config, ...updates.config }
            : tile.config; // If no config updates, keep existing config

          return {
            ...tile,
            ...updates,
            config: newConfig, // Use the deeply merged config
          };
        }
        return tile;
      });

      // CRITICAL: Verify formId was properly set in state
      const updatedTile = updatedTiles.find(t => t.id === id);
      if (updates.config?.formId && updatedTile?.config?.formId) {
        // console.log('✅ UPDATE_TILE formId verified in state:', updatedTile.config.formId);
      }
      
      // INVESTIGATION: Track form tile state updates
      state.tiles.find(t => t.id === id);

      const updateTileState = {
        ...state,
        tiles: updatedTiles,
        hasUnsavedChanges: true,
      };
      logStateChange('UPDATE_TILE', state.hasUnsavedChanges, updateTileState.hasUnsavedChanges);
      
      return addToHistory(updateTileState, 'UPDATE_TILE');
    }

    case 'REMOVE_TILE':
      
      // CRITICAL PROTECTION: Prevent removal of protected tiles
      if (state.protectedTiles.has(action.payload) || state.contentEditingTile === action.payload) {
        console.warn('🛡️ BLOCKED: Cannot remove protected tile:', action.payload, {
          isProtected: state.protectedTiles.has(action.payload),
          isBeingEdited: state.contentEditingTile === action.payload
        });
        return state; // Don't remove protected tiles
      }
      
      const removeTileState = {
        ...state,
        tiles: state.tiles.filter(tile => tile.id !== action.payload),
        selection: state.selection.type === 'tile' && state.selection.id === action.payload
          ? { type: 'none' as SelectionType }
          : state.selection,
        hasUnsavedChanges: true,
      };
      
      return addToHistory(removeTileState, 'REMOVE_TILE');

    case 'MOVE_TILE':
      const tileToMove = state.tiles.find(t => t.id === action.payload.id);
      if (!tileToMove) {
        console.warn('MOVE_TILE: Tile not found:', action.payload.id);
        return state;
      }

      if (
        tileToMove.type === 'workflow_step' &&
        action.payload.position.column === 1 &&
        state.tiles.some(tile =>
          tile.id !== tileToMove.id &&
          tile.type === 'workflow_step' &&
          tile.position.column === 1
        )
      ) {
        return state;
      }

      if (tileToMove.type === 'workflow_bypass' && action.payload.position.column === 1) {
        return state;
      }

      if (!canPlaceTile(state.layout, state.tiles, action.payload.position, tileToMove.size, tileToMove.id)) {
        return {
          ...state,
          draggedTile: undefined,
        };
      }

      let newState = {
        ...state,
        tiles: state.tiles.map(tile =>
          tile.id === action.payload.id
            ? { ...tile, position: action.payload.position }
            : tile
        ),
        hasUnsavedChanges: true,
      };

      return addToHistory(newState, 'MOVE_TILE');

    case 'RESIZE_TILE':
      return {
        ...state,
        tiles: state.tiles.map(tile =>
          tile.id === action.payload.id
            ? { ...tile, size: action.payload.size }
            : tile
        ),
        hasUnsavedChanges: true,
      };

    case 'SET_SELECTION':
      return {
        ...state,
        selection: action.payload,
      };

    case 'TOGGLE_TILE_SELECTION':
      // Toggle tile in multi-select array
      const isCurrentlySelected = state.selectedTiles.includes(action.payload);
      return {
        ...state,
        selectedTiles: isCurrentlySelected
          ? state.selectedTiles.filter(id => id !== action.payload)
          : [...state.selectedTiles, action.payload],
        // If adding first tile, also set single selection for backward compatibility
        selection: state.selectedTiles.length === 0 && !isCurrentlySelected
          ? { type: 'tile', id: action.payload }
          : state.selection
      };

    case 'SET_SELECTED_TILES':
      return {
        ...state,
        selectedTiles: action.payload,
        // Update single selection if only one tile selected
        selection: action.payload.length === 1
          ? { type: 'tile', id: action.payload[0] }
          : action.payload.length === 0
          ? { type: 'none' }
          : state.selection
      };

    case 'CLEAR_SELECTED_TILES':
      return {
        ...state,
        selectedTiles: [],
        selection: { type: 'none' }
      };

    case 'UPDATE_SELECTED_TILES':
      // Batch update all selected tiles
      return {
        ...state,
        tiles: state.tiles.map(tile =>
          state.selectedTiles.includes(tile.id)
            ? { ...tile, ...action.payload.updates }
            : tile
        ),
        hasUnsavedChanges: true
      };

    case 'REMOVE_SELECTED_TILES':
      // Batch delete all selected tiles
      const removeTilesState = {
        ...state,
        tiles: state.tiles.filter(tile => !state.selectedTiles.includes(tile.id)),
        selectedTiles: [],
        selection: { type: 'none' as SelectionType },
        hasUnsavedChanges: true
      };
      return addToHistory(removeTilesState, 'REMOVE_SELECTED_TILES');

    case 'START_DRAG':
      return {
        ...state,
        draggedTile: {
          id: action.payload.id,
          startPosition: action.payload.position,
          currentPosition: action.payload.position,
        },
      };

    case 'UPDATE_DRAG':
      return {
        ...state,
        draggedTile: state.draggedTile
          ? { ...state.draggedTile, currentPosition: action.payload }
          : undefined,
      };

    case 'END_DRAG':
      return {
        ...state,
        draggedTile: undefined,
      };

    case 'START_RESIZE':
      return {
        ...state,
        resizingTile: {
          id: action.payload.id,
          startSize: action.payload.size,
          currentSize: action.payload.size,
          startPosition: action.payload.position,
          currentPosition: action.payload.position,
        },
      };

    case 'UPDATE_RESIZE':
      return {
        ...state,
        resizingTile: state.resizingTile
          ? { 
              ...state.resizingTile, 
              currentSize: action.payload.size,
              currentPosition: action.payload.position 
            }
          : undefined,
      };

    case 'END_RESIZE':
      return {
        ...state,
        resizingTile: undefined,
      };

    case 'ADD_ROW': {
      const newRowIndex = state.layout.rows + 1;
      const addRowState = {
        ...state,
        layout: {
          ...state.layout,
          rows: newRowIndex,
          rowHeaders: state.layout.showRowHeaders
            ? (() => {
                if (state.layout.rowHeaders && state.layout.rowHeaders.length > 0) {
                  // Add a new header for the new row at the end
                  const newHeader: GridHeader = {
                    id: `row-header-${newRowIndex}-${Date.now()}`,
                    index: newRowIndex,
                    type: 'row'
                  };
                  return [...state.layout.rowHeaders, newHeader];
                } else {
                  return createDefaultHeaders('row', newRowIndex);
                }
              })()
            : state.layout.rowHeaders || []
        },
        hasUnsavedChanges: true,
      };
      return addToHistory(addRowState, 'ADD_ROW');
    }

    case 'REMOVE_ROW': {
      const removedRowIndex = action.payload;
      const newRowCount = Math.max(1, state.layout.rows - 1);
      
      // Adjust tiles for the removed row
      const adjustedTiles = adjustTilesForRowRemoval(state.tiles, removedRowIndex);
      
      // Adjust and clean up row headers
      let adjustedRowHeaders: any[] = [];
      if (state.layout.rowHeaders && state.layout.rowHeaders.length > 0) {
        adjustedRowHeaders = adjustHeadersForRowRemoval(state.layout.rowHeaders, removedRowIndex);
      }
      
      // Adjust and clean up row categories
      let adjustedRowCategories: GridCategory[] = [];
      if (state.layout.rowCategories && state.layout.rowCategories.length > 0) {
        adjustedRowCategories = adjustCategoriesForRowRemoval(state.layout.rowCategories, removedRowIndex)
          .filter(category => 
            category.startIndex >= 1 && 
            category.startIndex <= newRowCount && 
            category.endIndex >= 1 && 
            category.endIndex <= newRowCount &&
            category.startIndex <= category.endIndex
          );
      }
      
      const removeRowState = {
        ...state,
        layout: {
          ...state.layout,
          rows: newRowCount,
          rowHeaders: adjustedRowHeaders,
          rowCategories: adjustedRowCategories
        },
        tiles: adjustedTiles,
        hasUnsavedChanges: true,
      };
      return addToHistory(removeRowState, 'REMOVE_ROW');
    }

    case 'ADD_COLUMN': {
      const newColumnIndex = state.layout.columns + 1;
      const addColumnState = {
        ...state,
        layout: {
          ...state.layout,
          columns: newColumnIndex,
          columnHeaders: state.layout.showColumnHeaders
            ? (() => {
                if (state.layout.columnHeaders && state.layout.columnHeaders.length > 0) {
                  // Add a new header for the new column at the end
                  const newHeader: GridHeader = {
                    id: `column-header-${newColumnIndex}-${Date.now()}`,
                    index: newColumnIndex,
                    type: 'column'
                  };
                  return [...state.layout.columnHeaders, newHeader];
                } else {
                  return createDefaultHeaders('column', newColumnIndex);
                }
              })()
            : state.layout.columnHeaders || []
        },
        hasUnsavedChanges: true,
      };
      return addToHistory(addColumnState, 'ADD_COLUMN');
    }

    case 'REMOVE_COLUMN': {
      const removedColumnIndex = action.payload;
      const newColumnCount = Math.max(1, state.layout.columns - 1);
      
      // Adjust tiles for the removed column
      const adjustedTiles = adjustTilesForColumnRemoval(state.tiles, removedColumnIndex);
      
      // Adjust and clean up column headers
      let adjustedColumnHeaders: any[] = [];
      if (state.layout.columnHeaders && state.layout.columnHeaders.length > 0) {
        adjustedColumnHeaders = adjustHeadersForColumnRemoval(state.layout.columnHeaders, removedColumnIndex);
      }
      
      // Adjust and clean up column categories
      let adjustedColumnCategories: GridCategory[] = [];
      if (state.layout.columnCategories && state.layout.columnCategories.length > 0) {
        adjustedColumnCategories = adjustCategoriesForColumnRemoval(state.layout.columnCategories, removedColumnIndex)
          .filter(category => 
            category.startIndex >= 1 && 
            category.startIndex <= newColumnCount && 
            category.endIndex >= 1 && 
            category.endIndex <= newColumnCount &&
            category.startIndex <= category.endIndex
          );
      }
      
      const removeColumnState = {
        ...state,
        layout: {
          ...state.layout,
          columns: newColumnCount,
          columnHeaders: adjustedColumnHeaders,
          columnCategories: adjustedColumnCategories
        },
        tiles: adjustedTiles,
        hasUnsavedChanges: true,
      };
      return addToHistory(removeColumnState, 'REMOVE_COLUMN');
    }

    case 'INSERT_ROW_AT': {
      const insertionRow = action.payload;
      const adjustedTiles = adjustTilesForRowInsertion(state.tiles, insertionRow);
      const resolvedTiles = resolveCollisionsAfterInsertion(
        { ...state.layout, rows: state.layout.rows + 1 },
        adjustedTiles
      );
      
      // Adjust row categories for insertion
      let adjustedRowCategories: GridCategory[] = [];
      if (state.layout.rowCategories && state.layout.rowCategories.length > 0) {
        adjustedRowCategories = adjustCategoriesForRowInsertion(state.layout.rowCategories, insertionRow);
      }

      const insertRowState = {
        ...state,
        layout: {
          ...state.layout,
          rows: state.layout.rows + 1,
          rowHeaders: state.layout.showRowHeaders
            ? (() => {
                if (state.layout.rowHeaders && state.layout.rowHeaders.length > 0) {
                  // Adjust existing headers and create a new one for the inserted row
                  const adjustedHeaders = adjustHeadersForRowInsertion(state.layout.rowHeaders, insertionRow);
                  const newHeader: GridHeader = {
                    id: `row-header-${insertionRow}-${Date.now()}`,
                    index: insertionRow,
                    type: 'row'
                  };
                  return [...adjustedHeaders, newHeader].sort((a, b) => a.index - b.index);
                } else {
                  return createDefaultHeaders('row', state.layout.rows + 1);
                }
              })()
            : [],
          rowCategories: adjustedRowCategories
        },
        tiles: resolvedTiles,
        hasUnsavedChanges: true,
      };
      return addToHistory(insertRowState, 'INSERT_ROW_AT');
    }

    case 'INSERT_COLUMN_AT': {
      const insertionColumn = action.payload;
      const adjustedTiles = adjustTilesForColumnInsertion(state.tiles, insertionColumn);
      const resolvedTiles = resolveCollisionsAfterInsertion(
        { ...state.layout, columns: state.layout.columns + 1 },
        adjustedTiles
      );
      
      // Adjust column categories for insertion
      let adjustedColumnCategories: GridCategory[] = [];
      if (state.layout.columnCategories && state.layout.columnCategories.length > 0) {
        adjustedColumnCategories = adjustCategoriesForColumnInsertion(state.layout.columnCategories, insertionColumn);
      }

      const insertColumnState = {
        ...state,
        layout: {
          ...state.layout,
          columns: state.layout.columns + 1,
          columnHeaders: state.layout.showColumnHeaders
            ? (() => {
                if (state.layout.columnHeaders && state.layout.columnHeaders.length > 0) {
                  // Adjust existing headers and create a new one for the inserted column
                  const adjustedHeaders = adjustHeadersForColumnInsertion(state.layout.columnHeaders, insertionColumn);
                  const newHeader: GridHeader = {
                    id: `column-header-${insertionColumn}-${Date.now()}`,
                    index: insertionColumn,
                    type: 'column'
                  };
                  return [...adjustedHeaders, newHeader].sort((a, b) => a.index - b.index);
                } else {
                  return createDefaultHeaders('column', state.layout.columns + 1);
                }
              })()
            : [],
          columnCategories: adjustedColumnCategories
        },
        tiles: resolvedTiles,
        hasUnsavedChanges: true,
      };
      return addToHistory(insertColumnState, 'INSERT_COLUMN_AT');
    }

    case 'REMOVE_ROW_AT': {
      const removedRowIndex = action.payload;
      const newRowCount = Math.max(1, state.layout.rows - 1);
      
      // Adjust tiles for the removed row (using existing logic)
      const adjustedTiles = adjustTilesForRowRemoval(state.tiles, removedRowIndex);
      
      // Adjust and clean up row headers
      let adjustedRowHeaders: any[] = [];
      if (state.layout.rowHeaders && state.layout.rowHeaders.length > 0) {
        adjustedRowHeaders = adjustHeadersForRowRemoval(state.layout.rowHeaders, removedRowIndex)
          .filter(header => {
            const startIndex = header.startIndex ?? header.index ?? 1;
            const endIndex = header.endIndex ?? header.index ?? startIndex;
            return startIndex >= 1 && 
              startIndex <= newRowCount && 
              endIndex >= 1 && 
              endIndex <= newRowCount &&
              startIndex <= endIndex;
          });
      }
      
      // Adjust and clean up row categories
      let adjustedRowCategories: GridCategory[] = [];
      if (state.layout.rowCategories && state.layout.rowCategories.length > 0) {
        adjustedRowCategories = adjustCategoriesForRowRemoval(state.layout.rowCategories, removedRowIndex)
          .filter(category => 
            category.startIndex >= 1 && 
            category.startIndex <= newRowCount && 
            category.endIndex >= 1 && 
            category.endIndex <= newRowCount &&
            category.startIndex <= category.endIndex
          );
      }
      
      const removeRowAtState = {
        ...state,
        layout: {
          ...state.layout,
          rows: newRowCount,
          rowHeaders: adjustedRowHeaders,
          rowCategories: adjustedRowCategories
        },
        tiles: adjustedTiles,
        hasUnsavedChanges: true,
      };
      return addToHistory(removeRowAtState, 'REMOVE_ROW_AT');
    }

    case 'REMOVE_COLUMN_AT': {
      const removedColumnIndex = action.payload;
      const newColumnCount = Math.max(1, state.layout.columns - 1);
      
      // Adjust tiles for the removed column (using existing logic)
      const adjustedTiles = adjustTilesForColumnRemoval(state.tiles, removedColumnIndex);
      
      // Adjust and clean up column headers
      let adjustedColumnHeaders: any[] = [];
      if (state.layout.columnHeaders && state.layout.columnHeaders.length > 0) {
        adjustedColumnHeaders = adjustHeadersForColumnRemoval(state.layout.columnHeaders, removedColumnIndex)
          .filter(header => {
            const startIndex = header.startIndex ?? header.index ?? 1;
            const endIndex = header.endIndex ?? header.index ?? startIndex;
            return startIndex >= 1 && 
              startIndex <= newColumnCount && 
              endIndex >= 1 && 
              endIndex <= newColumnCount &&
              startIndex <= endIndex;
          });
      }
      
      // Adjust and clean up column categories
      let adjustedColumnCategories: GridCategory[] = [];
      if (state.layout.columnCategories && state.layout.columnCategories.length > 0) {
        adjustedColumnCategories = adjustCategoriesForColumnRemoval(state.layout.columnCategories, removedColumnIndex)
          .filter(category => 
            category.startIndex >= 1 && 
            category.startIndex <= newColumnCount && 
            category.endIndex >= 1 && 
            category.endIndex <= newColumnCount &&
            category.startIndex <= category.endIndex
          );
      }
      
      const removeColumnAtState = {
        ...state,
        layout: {
          ...state.layout,
          columns: newColumnCount,
          columnHeaders: adjustedColumnHeaders,
          columnCategories: adjustedColumnCategories
        },
        tiles: adjustedTiles,
        hasUnsavedChanges: true,
      };
      return addToHistory(removeColumnAtState, 'REMOVE_COLUMN_AT');
    }

    case 'TOGGLE_ROW_HEADERS': {
      const showRowHeaders = action.payload !== undefined ? action.payload : !state.layout.showRowHeaders;
      // Removed verbose row headers logging
      
      const toggleState = {
        ...state,
        layout: {
          ...state.layout,
          showRowHeaders,
          rowHeaders: showRowHeaders 
            ? ((!state.layout.rowHeaders || state.layout.rowHeaders.length === 0)
                ? createDefaultHeaders('row', state.layout.rows)
                : state.layout.rowHeaders)
            : [] // Clear headers when turning OFF
        },
        hasUnsavedChanges: true,
      };
      
      
      return addToHistory(toggleState, 'TOGGLE_ROW_HEADERS');
    }

    case 'TOGGLE_COLUMN_HEADERS': {
      const showColumnHeaders = action.payload !== undefined ? action.payload : !state.layout.showColumnHeaders;
      const toggleState = {
        ...state,
        layout: {
          ...state.layout,
          showColumnHeaders,
          columnHeaders: showColumnHeaders && (!state.layout.columnHeaders || state.layout.columnHeaders.length === 0)
            ? createDefaultHeaders('column', state.layout.columns)
            : state.layout.columnHeaders || []
        },
        hasUnsavedChanges: true,
      };
      return addToHistory(toggleState, 'TOGGLE_COLUMN_HEADERS');
    }

    case 'UPDATE_HEADER': {
      const { type, header } = action.payload;
      const headerKey = type === 'row' ? 'rowHeaders' : 'columnHeaders';
      const currentHeaders = state.layout[headerKey] || [];
      
      const updateState = {
        ...state,
        layout: {
          ...state.layout,
          [headerKey]: currentHeaders.map(h => h.id === header.id ? header : h)
        },
        hasUnsavedChanges: true,
      };
      return addToHistory(updateState, 'UPDATE_HEADER');
    }

    case 'MERGE_HEADERS': {
      // Headers are now simple containers - merge operations are handled by categories
      console.warn('MERGE_HEADERS is deprecated - use categories for header content and styling');
      return state;
    }

    case 'SPLIT_HEADER': {
      // Headers are now simple containers - split operations are handled by categories
      console.warn('SPLIT_HEADER is deprecated - use categories for header content and styling');
      return state;
    }

    case 'ADD_CATEGORY': {
      const { type, category } = action.payload;
      const categoryKey = type === 'row' ? 'rowCategories' : 'columnCategories';
      const currentCategories = state.layout[categoryKey] || [];
      
      const addCategoryState = {
        ...state,
        layout: {
          ...state.layout,
          [categoryKey]: [...currentCategories, category]
        },
        hasUnsavedChanges: true,
      };
      return addToHistory(addCategoryState, 'ADD_CATEGORY');
    }

    case 'UPDATE_CATEGORY': {
      const { type, category } = action.payload;
      // console.log('🔄 Reducer UPDATE_CATEGORY:', {
      //   type,
      //   categoryId: category.id,
      //   gradientColor: category.style?.gradientColor,
      //   outlineColor: category.style?.outlineColor
      // });
      const categoryKey = type === 'row' ? 'rowCategories' : 'columnCategories';
      const currentCategories = state.layout[categoryKey] || [];
      
      const updatedCategories = currentCategories.map(c => c.id === category.id ? category : c);
      // console.log('🔄 Updated categories:', updatedCategories.find(c => c.id === category.id));
      
      const updateCategoryState = {
        ...state,
        layout: {
          ...state.layout,
          [categoryKey]: updatedCategories
        },
        hasUnsavedChanges: true,
      };
      return addToHistory(updateCategoryState, 'UPDATE_CATEGORY');
    }

    case 'REMOVE_CATEGORY': {
      const { type, categoryId } = action.payload;
      const categoryKey = type === 'row' ? 'rowCategories' : 'columnCategories';
      const currentCategories = state.layout[categoryKey] || [];
      
      const removeCategoryState = {
        ...state,
        layout: {
          ...state.layout,
          [categoryKey]: currentCategories.filter(c => c.id !== categoryId)
        },
        hasUnsavedChanges: true,
      };
      return addToHistory(removeCategoryState, 'REMOVE_CATEGORY');
    }

    case 'MIGRATE_LEGACY_HEADERS': {
      const layout = state.layout;
      let updatedLayout = { ...layout };
      
      // Migrate legacy row headers if they exist
      if (layout.legacyRowHeaders && layout.legacyRowHeaders.length > 0) {
        const { headers, categories } = migrateLegacyHeaders(layout.legacyRowHeaders, 'row', layout.rows);
        updatedLayout.rowHeaders = headers;
        updatedLayout.rowCategories = categories;
        updatedLayout.legacyRowHeaders = undefined;
      }
      
      // Migrate legacy column headers if they exist
      if (layout.legacyColumnHeaders && layout.legacyColumnHeaders.length > 0) {
        const { headers, categories } = migrateLegacyHeaders(layout.legacyColumnHeaders, 'column', layout.columns);
        updatedLayout.columnHeaders = headers;
        updatedLayout.columnCategories = categories;
        updatedLayout.legacyColumnHeaders = undefined;
      }
      
      // Ensure headers exist for current grid dimensions
      updatedLayout = ensureHeadersExist(updatedLayout);
      
      const migrateState = {
        ...state,
        layout: updatedLayout,
        // Don't set hasUnsavedChanges for automatic migrations during initialization
        hasUnsavedChanges: false,
      };
      logStateChange('MIGRATE_LEGACY_HEADERS', state.hasUnsavedChanges, migrateState.hasUnsavedChanges);
      return addToHistory(migrateState, 'MIGRATE_LEGACY_HEADERS');
    }

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_UNSAVED_CHANGES':
      return {
        ...state,
        hasUnsavedChanges: action.payload,
      };

    case 'CLEANUP_HEADER_IDS':
      // console.log('CLEANUP_HEADER_IDS: Before cleanup', {
      //   rowHeaders: state.layout.rowHeaders?.map(h => ({ id: h.id, title: h.title })),
      //   columnHeaders: state.layout.columnHeaders?.map(h => ({ id: h.id, title: h.title }))
      // });
      
      // Legacy header formatting has been removed - categories handle styling now
      
      const cleanedState = {
        ...state,
        layout: {
          ...state.layout,
          rowHeaders: state.layout.rowHeaders ? ensureUniqueHeaderIds(state.layout.rowHeaders) : undefined,
          columnHeaders: state.layout.columnHeaders ? ensureUniqueHeaderIds(state.layout.columnHeaders) : undefined,
        }
      };
      
      // console.log('CLEANUP_HEADER_IDS: After cleanup', {
      //   rowHeaders: cleanedState.layout.rowHeaders?.map(h => ({ id: h.id, title: h.title })),
      //   columnHeaders: cleanedState.layout.columnHeaders?.map(h => ({ id: h.id, title: h.title }))
      // });
      
      return cleanedState;

    case 'RESET_STATE':
      // CRITICAL PROTECTION: Preserve tiles that are currently being edited
      const incomingTiles = action.payload.tiles || [];
      const resetProtectedTiles = state.protectedTiles || new Set();
      const resetEditingTile = state.contentEditingTile;
      
      // Merge protected tiles with incoming tiles
      let resetFinalTiles = [...incomingTiles];
      
      // If we have protected tiles, preserve them
      if (resetProtectedTiles.size > 0 || resetEditingTile) {
        
        // Preserve all currently protected tiles and the editing tile
        const tilesToPreserve = state.tiles.filter(tile => 
          resetProtectedTiles.has(tile.id) || tile.id === resetEditingTile
        );
        
        // Merge: start with incoming tiles, then add/update with preserved tiles
        const finalTileMap = new Map(incomingTiles.map(tile => [tile.id, tile]));
        
        tilesToPreserve.forEach(preservedTile => {
          finalTileMap.set(preservedTile.id, preservedTile);
        });
        
        resetFinalTiles = Array.from(finalTileMap.values());
        
      }
      
      const resetState = {
        ...initialState,
        ...action.payload,
        tiles: resetFinalTiles,
        // Preserve protection state
        protectedTiles: resetProtectedTiles,
        contentEditingTile: resetEditingTile,
      };
      
      const finalResetState = {
        ...resetState,
        history: {
          past: [],
          present: createHistoryEntry(resetState, 'RESET'),
          future: []
        }
      };
      logStateChange('RESET_STATE', state.hasUnsavedChanges, finalResetState.hasUnsavedChanges);
      return finalResetState;

    case 'UNDO':
      if (state.history.past.length === 0) {
        return state;
      }
      
      const previous = state.history.past[state.history.past.length - 1];
      const newPast = state.history.past.slice(0, -1);
      
      // CRITICAL PROTECTION: Preserve tiles being edited during UNDO
      let undoTiles = previous.tiles;
      const undoProtectedTiles = state.protectedTiles || new Set();
      const undoEditingTile = state.contentEditingTile;
      
      if (undoProtectedTiles.size > 0 || undoEditingTile) {
        
        // Preserve protected tiles from current state
        const tilesToPreserve = state.tiles.filter(tile => 
          undoProtectedTiles.has(tile.id) || tile.id === undoEditingTile
        );
        
        // Merge with undo tiles
        const undoTileMap = new Map(previous.tiles.map(tile => [tile.id, tile]));
        tilesToPreserve.forEach(preservedTile => {
          undoTileMap.set(preservedTile.id, preservedTile);
        });
        
        undoTiles = Array.from(undoTileMap.values());
      }
      
      return {
        ...state,
        layout: previous.layout,
        tiles: undoTiles,
        history: {
          past: newPast,
          present: previous,
          future: [state.history.present, ...state.history.future]
        },
        hasUnsavedChanges: true
      };

    case 'REDO':
      if (state.history.future.length === 0) {
        return state;
      }
      
      const next = state.history.future[0];
      const newFuture = state.history.future.slice(1);
      
      // CRITICAL PROTECTION: Preserve tiles being edited during REDO
      let redoTiles = next.tiles;
      const redoProtectedTiles = state.protectedTiles || new Set();
      const redoEditingTile = state.contentEditingTile;
      
      if (redoProtectedTiles.size > 0 || redoEditingTile) {
        console.log('🛡️ REDO Protection Active:', {
          protectedTiles: Array.from(redoProtectedTiles),
          editingTile: redoEditingTile
        });
        
        // Preserve protected tiles from current state
        const tilesToPreserve = state.tiles.filter(tile => 
          redoProtectedTiles.has(tile.id) || tile.id === redoEditingTile
        );
        
        // Merge with redo tiles
        const redoTileMap = new Map(next.tiles.map(tile => [tile.id, tile]));
        tilesToPreserve.forEach(preservedTile => {
          redoTileMap.set(preservedTile.id, preservedTile);
        });
        
        redoTiles = Array.from(redoTileMap.values());
      }
      
      return {
        ...state,
        layout: next.layout,
        tiles: redoTiles,
        history: {
          past: [...state.history.past, state.history.present],
          present: next,
          future: newFuture
        },
        hasUnsavedChanges: true
      };

    case 'CLEAR_HISTORY':
      return {
        ...state,
        history: {
          past: [],
          present: createHistoryEntry(state, 'CLEAR_HISTORY'),
          future: []
        }
      };

    case 'PROTECT_TILE':
      const newProtectedSet = new Set(state.protectedTiles);
      newProtectedSet.add(action.payload);
      return {
        ...state,
        protectedTiles: newProtectedSet
      };

    case 'UNPROTECT_TILE':
      const newProtectedTiles = new Set(state.protectedTiles);
      newProtectedTiles.delete(action.payload);
      return {
        ...state,
        protectedTiles: newProtectedTiles
      };

    case 'START_CONTENT_EDITING':
      
      const editingProtectedSet = new Set(state.protectedTiles);
      editingProtectedSet.add(action.payload);
      
      
      return {
        ...state,
        contentEditingTile: action.payload,
        protectedTiles: editingProtectedSet
      };

    case 'END_CONTENT_EDITING':
      
      const updatedProtectedTiles = new Set(state.protectedTiles);
      if (state.contentEditingTile) {
        updatedProtectedTiles.delete(state.contentEditingTile);
      }
      
      
      return {
        ...state,
        contentEditingTile: undefined,
        protectedTiles: updatedProtectedTiles
      };

    case 'SHOW_NAVIGATION_WARNING':
      return {
        ...state,
        navigationWarning: {
          isOpen: true,
          destinationUrl: action.payload.destinationUrl,
          destinationTitle: action.payload.destinationTitle,
          isSaving: false
        }
      };

    case 'HIDE_NAVIGATION_WARNING':
      return {
        ...state,
        navigationWarning: undefined
      };

    case 'SET_NAVIGATION_SAVING':
      return {
        ...state,
        navigationWarning: state.navigationWarning ? {
          ...state.navigationWarning,
          isSaving: action.payload
        } : undefined
      };

    // Duplicate cases removed - ADD_CATEGORY and REMOVE_CATEGORY are handled earlier in the reducer

    default:
      return state;
  }
}

interface GridContextValue {
  state: GridState;
  dispatch: React.Dispatch<GridAction>;
  getLatestState: () => GridState;
  // Convenience methods
  setMode: (mode: GridMode) => void;
  selectTile: (id: string) => void;
  selectRow: (index: number) => void;
  selectColumn: (index: number) => void;
  selectRowHeader: (headerId: string) => void;
  selectColumnHeader: (headerId: string) => void;
  clearSelection: () => void;
  // Multi-select methods
  toggleTileSelection: (id: string) => void;
  setSelectedTiles: (ids: string[]) => void;
  clearSelectedTiles: () => void;
  updateSelectedTiles: (updates: Partial<GridTile>) => void;
  removeSelectedTiles: () => void;
  addTile: (tile: Omit<GridTile, 'id'>) => void;
  restoreTile: (tile: GridTile) => void;
  removeTile: (id: string) => void;
  moveTile: (id: string, position: GridPosition) => void;
  resizeTile: (id: string, size: GridSize) => void;
  addRow: (appearanceDefaults?: any) => void;
  removeRow: (index: number) => void;
  addColumn: (appearanceDefaults?: any) => void;
  removeColumn: (index: number) => void;
  insertRowAt: (position: number, appearanceDefaults?: any) => void;
  insertColumnAt: (position: number, appearanceDefaults?: any) => void;
  removeRowAt: (position: number) => void;
  removeColumnAt: (position: number) => void;
  toggleRowHeaders: (show?: boolean, appearanceDefaults?: any) => void;
  toggleColumnHeaders: (show?: boolean, appearanceDefaults?: any) => void;
  updateHeader: (type: 'row' | 'column', header: GridHeader) => void;
  mergeHeaders: (type: 'row' | 'column', headerIds: string[], newTitle: string) => void;
  splitHeader: (type: 'row' | 'column', headerId: string, splitIndex: number) => void;
  // Category management functions
  selectRowCategory: (categoryId: string) => void;
  selectColumnCategory: (categoryId: string) => void;
  addCategory: (type: 'row' | 'column', category: GridCategory) => void;
  updateCategory: (type: 'row' | 'column', category: GridCategory) => void;
  removeCategory: (type: 'row' | 'column', categoryId: string) => void;
  migrateLegacyHeaders: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  showNavigationWarning: (destinationUrl: string, destinationTitle?: string) => void;
  hideNavigationWarning: () => void;
  setNavigationSaving: (isSaving: boolean) => void;
}

const GridContext = createContext<GridContextValue | undefined>(undefined);

export function GridProvider({ children }: { children: React.ReactNode }) {
  const latestStateRef = useRef<GridState>(initialState);
  const [state, setState] = useState<GridState>(initialState);

  const dispatch = useCallback<React.Dispatch<GridAction>>((action) => {
    const nextState = gridReducer(latestStateRef.current, action);
    latestStateRef.current = nextState;
    setState(nextState);
  }, []);

  const getLatestState = useCallback(() => latestStateRef.current, []);

  // Debug: Log initial state (can be removed after testing)
  // console.log('🏁 GridProvider initialized with hasUnsavedChanges:', initialState.hasUnsavedChanges);

  // Clean up duplicate header IDs on mount
  useEffect(() => {
    // console.log('🧹 GridProvider: Running CLEANUP_HEADER_IDS on mount');
    dispatch({ type: 'CLEANUP_HEADER_IDS' });
  }, []);

  const setMode = useCallback((mode: GridMode) => {
    dispatch({ type: 'SET_MODE', payload: mode });
  }, []);

  const selectTile = useCallback((id: string) => {
    dispatch({ type: 'SET_SELECTION', payload: { type: 'tile', id } });
  }, []);

  const selectRow = useCallback((index: number) => {
    dispatch({ type: 'SET_SELECTION', payload: { type: 'row', index } });
  }, []);

  const selectColumn = useCallback((index: number) => {
    dispatch({ type: 'SET_SELECTION', payload: { type: 'column', index } });
  }, []);

  const selectRowHeader = useCallback((headerId: string) => {
    dispatch({ type: 'SET_SELECTION', payload: { type: 'rowHeader', headerId } });
  }, []);

  const selectColumnHeader = useCallback((headerId: string) => {
    dispatch({ type: 'SET_SELECTION', payload: { type: 'columnHeader', headerId } });
  }, []);

  const clearSelection = useCallback(() => {
    dispatch({ type: 'SET_SELECTION', payload: { type: 'none' } });
  }, []);

  // Multi-select convenience methods
  const toggleTileSelection = useCallback((id: string) => {
    dispatch({ type: 'TOGGLE_TILE_SELECTION', payload: id });
  }, []);

  const setSelectedTiles = useCallback((ids: string[]) => {
    dispatch({ type: 'SET_SELECTED_TILES', payload: ids });
  }, []);

  const clearSelectedTiles = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTED_TILES' });
  }, []);

  const updateSelectedTiles = useCallback((updates: Partial<GridTile>) => {
    dispatch({ type: 'UPDATE_SELECTED_TILES', payload: { updates } });
  }, []);

  const removeSelectedTiles = useCallback(() => {
    dispatch({ type: 'REMOVE_SELECTED_TILES' });
  }, []);

  const addTile = useCallback((tile: Omit<GridTile, 'id'>) => {
    const newTile: GridTile = {
      ...tile,
      id: `tile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    dispatch({ type: 'ADD_TILE', payload: newTile });
  }, []);

  const restoreTile = useCallback((tile: GridTile) => {
    dispatch({ type: 'RESTORE_TILE', payload: tile });
  }, []);

  const removeTile = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TILE', payload: id });
  }, []);

  const moveTile = useCallback((id: string, position: GridPosition) => {
    dispatch({ type: 'MOVE_TILE', payload: { id, position } });
  }, []);

  const resizeTile = useCallback((id: string, size: GridSize) => {
    dispatch({ type: 'RESIZE_TILE', payload: { id, size } });
  }, []);

  const addRow = useCallback((appearanceDefaults?: any) => {
    dispatch({ type: 'ADD_ROW', appearanceDefaults });
  }, []);

  const removeRow = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_ROW', payload: index });
  }, []);

  const addColumn = useCallback((appearanceDefaults?: any) => {
    dispatch({ type: 'ADD_COLUMN', appearanceDefaults });
  }, []);

  const removeColumn = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_COLUMN', payload: index });
  }, []);

  const insertRowAt = useCallback((position: number, appearanceDefaults?: any) => {
    dispatch({ type: 'INSERT_ROW_AT', payload: position, appearanceDefaults });
  }, []);

  const insertColumnAt = useCallback((position: number, appearanceDefaults?: any) => {
    dispatch({ type: 'INSERT_COLUMN_AT', payload: position, appearanceDefaults });
  }, []);

  const removeRowAt = useCallback((position: number) => {
    dispatch({ type: 'REMOVE_ROW_AT', payload: position });
  }, []);

  const removeColumnAt = useCallback((position: number) => {
    dispatch({ type: 'REMOVE_COLUMN_AT', payload: position });
  }, []);

  const toggleRowHeaders = useCallback((show?: boolean, appearanceDefaults?: any) => {
    dispatch({ type: 'TOGGLE_ROW_HEADERS', payload: show, appearanceDefaults });
  }, []);

  const toggleColumnHeaders = useCallback((show?: boolean, appearanceDefaults?: any) => {
    dispatch({ type: 'TOGGLE_COLUMN_HEADERS', payload: show, appearanceDefaults });
  }, []);

  const updateHeader = useCallback((type: 'row' | 'column', header: GridHeader) => {
    dispatch({ type: 'UPDATE_HEADER', payload: { type, header } });
  }, []);

  const mergeHeaders = useCallback((type: 'row' | 'column', headerIds: string[], newTitle: string) => {
    dispatch({ type: 'MERGE_HEADERS', payload: { type, headerIds, newTitle } });
  }, []);

  const splitHeader = useCallback((type: 'row' | 'column', headerId: string, splitIndex: number) => {
    dispatch({ type: 'SPLIT_HEADER', payload: { type, headerId, splitIndex } });
  }, []);

  // Category management functions
  const selectRowCategory = useCallback((categoryId: string) => {
    dispatch({ type: 'SET_SELECTION', payload: { type: 'rowCategory', categoryId } });
  }, []);

  const selectColumnCategory = useCallback((categoryId: string) => {
    dispatch({ type: 'SET_SELECTION', payload: { type: 'columnCategory', categoryId } });
  }, []);

  const addCategory = useCallback((type: 'row' | 'column', category: GridCategory) => {
    dispatch({ type: 'ADD_CATEGORY', payload: { type, category } });
  }, []);

  const updateCategory = useCallback((type: 'row' | 'column', category: GridCategory) => {
    // console.log('🔄 GridContext updateCategory:', {
    //   type,
    //   categoryId: category.id,
    //   gradientColor: category.style?.gradientColor,
    //   outlineColor: category.style?.outlineColor,
    //   fullStyle: category.style
    // });
    dispatch({ type: 'UPDATE_CATEGORY', payload: { type, category } });
  }, []);

  const removeCategory = useCallback((type: 'row' | 'column', categoryId: string) => {
    dispatch({ type: 'REMOVE_CATEGORY', payload: { type, categoryId } });
  }, []);

  const migrateLegacyHeaders = useCallback(() => {
    dispatch({ type: 'MIGRATE_LEGACY_HEADERS' });
  }, []);

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, []);

  const canUndo = state.history.past.length > 0;
  const canRedo = state.history.future.length > 0;

  // Navigation warning functions
  const showNavigationWarning = useCallback((destinationUrl: string, destinationTitle?: string) => {
    dispatch({ 
      type: 'SHOW_NAVIGATION_WARNING', 
      payload: { destinationUrl, destinationTitle } 
    });
  }, []);

  const hideNavigationWarning = useCallback(() => {
    dispatch({ type: 'HIDE_NAVIGATION_WARNING' });
  }, []);

  const setNavigationSaving = useCallback((isSaving: boolean) => {
    dispatch({ type: 'SET_NAVIGATION_SAVING', payload: isSaving });
  }, []);

  // Auto-save functionality
  useEffect(() => {
    if (state.hasUnsavedChanges) {
      const timeoutId = setTimeout(() => {
        // TODO: Implement auto-save API call

      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [state.hasUnsavedChanges, state.layout, state.tiles]);

  const value: GridContextValue = {
    state,
    dispatch,
    getLatestState,
    setMode,
    selectTile,
    selectRow,
    selectColumn,
    selectRowHeader,
    selectColumnHeader,
    clearSelection,
    toggleTileSelection,
    setSelectedTiles,
    clearSelectedTiles,
    updateSelectedTiles,
    removeSelectedTiles,
    addTile,
    restoreTile,
    removeTile,
    moveTile,
    resizeTile,
    addRow,
    removeRow,
    addColumn,
    removeColumn,
    insertRowAt,
    insertColumnAt,
    removeRowAt,
    removeColumnAt,
    toggleRowHeaders,
    toggleColumnHeaders,
    updateHeader,
    mergeHeaders,
    splitHeader,
    selectRowCategory,
    selectColumnCategory,
    addCategory,
    updateCategory,
    removeCategory,
    migrateLegacyHeaders,
    undo,
    redo,
    canUndo,
    canRedo,
    showNavigationWarning,
    hideNavigationWarning,
    setNavigationSaving,
  };

  return <GridContext.Provider value={value}>{children}</GridContext.Provider>;
}

export function useGrid() {
  const context = useContext(GridContext);
  if (context === undefined) {
    throw new Error('useGrid must be used within a GridProvider');
  }
  return context;
}

export function useGridState() {
  const { state } = useGrid();
  return state;
}

export function useGridActions() {
  const { dispatch, ...actions } = useGrid();
  return actions;
}
